// Tiny route guard. The mobile client has the same idea implemented as
// `redirect` on `go_router`; the web version uses an `Outlet` wrapper
// because React Router doesn't expose an analogous hook.
import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';

export function RequireAuth({ children }: { children?: ReactNode }) {
  const { isAuthenticated, state } = useAuth();
  const location = useLocation();
  // Show a blocking spinner for both initial load (unknown) and in-flight
  // refresh (refreshing).  The user is still authenticated during
  // refreshing — we must NOT redirect to /login, which would cause a
  // visible flash on every 401-triggered token refresh.
  if (state.status === 'unknown' || state.status === 'refreshing') {
    return <div className="empty">Checking session…</div>;
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return children ? <>{children}</> : <Outlet />;
}

export function RedirectIfAuthenticated({ children }: { children?: ReactNode }) {
  const { isAuthenticated, state } = useAuth();
  // Show a blocking spinner during both unknown and refreshing so that a
  // user who lands on /login with an active (but refreshing) session does
  // not briefly see the login form before being redirected to /home.
  if (state.status === 'unknown' || state.status === 'refreshing') {
    return <div className="empty">Checking session…</div>;
  }
  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }
  return children ? <>{children}</> : <Outlet />;
}
